"use client";
import type { IResource } from "@gettactic/api";
import { useBooleanState } from "@gettactic/helpers";
import type { UseSvgMapInteractionsReturn } from "@gettactic/helpers/src/map/svg/useSvgMapInteractions";
import { SvgMiniMap } from "@gettactic/map-builder/src/components/map/MapCanvas/SvgMiniMap";
import { useMap } from "@/features/map/MapProvider";
import React from "react";
import { createPortal } from "react-dom";

export interface MapViewSvgMiniMapProps {
  mapInteractor: UseSvgMapInteractionsReturn;
  width?: number;
  height?: number;
  disableOnFocus?: boolean;
  portalTarget?: HTMLElement | null;
  embedded?: boolean;
}

export const MapViewSvgMiniMap: React.FC<MapViewSvgMiniMapProps> = ({
  mapInteractor,
  width,
  height,
  disableOnFocus = true,
  portalTarget,
  embedded = false
}) => {
  const { events, startDate, floor, currentOffice } = useMap();

  const isDisabled = useBooleanState(false);

  React.useEffect(() => {
    if (!disableOnFocus) return;

    const onFocusResource = (resource: IResource | null) => {
      if (!resource) {
        isDisabled.false();
        return;
      }

      isDisabled.true();
    };
    events.register("onFocusResource", onFocusResource);

    return () => {
      events.unregister("onFocusResource", onFocusResource);
    };
  }, [startDate, floor, currentOffice, disableOnFocus]);

  const content = (
    <SvgMiniMap
      mapInteractor={mapInteractor}
      disabled={isDisabled.state}
      width={width}
      height={height}
      embedded={embedded}
    />
  );

  // If portalTarget is provided, portal to it; otherwise portal to document.body
  return portalTarget
    ? createPortal(content, portalTarget)
    : createPortal(content, window.document.body);
};
